import { Scene, TransformNode } from "babylonjs";
import {
  IAssetAudio,
  IAssetBase,
  IAssetCubeTexture,
  IAssetModel,
  IAssetPointCloud,
  IAssetTexture,
  IAssetVideo,
  loadAudio,
  loadCubeTexture,
  loadModel,
  loadPointCloud,
  loadTexture,
  loadVideo,
  unloadAudio,
  unloadCubeTexture,
  unloadModel,
  unloadPointCloud,
  unloadTexture,
  unloadVideo,
} from "./loader";

//----------------------------------------------------------------
// hooks the AssetBatch needs from its owner (eg. AppContent). live getters/
// callbacks rather than fixed values, so the batch stays decoupled from the
// owner's init lifecycle (scene/rootNode are created later) and always reads
// the current scene, root node and batch size.
export interface AssetBatchContext {
  getScene(): Scene | undefined;
  getRootNode(): TransformNode | undefined;
  getBatchSize?(): number | undefined; // undefined/0 -> load all at once.
  onAssetLoadComplete?(asset: IAssetBase): void; // overridable completion hook.
}

//----------------------------------------------------------------
// owns the in-scene asset registry and orchestrates (de-duplicated, optionally
// batched) loading/unloading by dispatching to the per-asset functions in
// loader.ts. extracted from AppContent so the same logic can be reused without
// the AppBabylon class chain; AppContent delegates to it.
export class AssetBatch {
  public readonly assets: IAssetBase[] = [];
  public readonly assetsLoading: IAssetBase[] = [];

  private context: AssetBatchContext;

  constructor(context: AssetBatchContext) {
    this.context = context;
  }

  //----------------------------------------------------------------
  public async loadContents(assetsToLoad: IAssetBase[]) {
    // check if any assets are already loading.
    // this is a work around the batching feature,
    // making sure assets can be removed from the async for-loop,
    // and are not loaded after being unloaded.
    for (const assetLoading of this.assetsLoading) {
      for (const assetToLoad of assetsToLoad) {
        if (assetLoading.path === assetToLoad.path) {
          const asssetToLoadIdx = assetsToLoad.indexOf(assetToLoad);
          assetsToLoad.splice(asssetToLoadIdx, 1);
        }
      }
    }
    this.assetsLoading.push(...assetsToLoad);
    // load assets.
    const batchSize = this.context.getBatchSize?.();
    if (batchSize) {
      while (this.assetsLoading.length > 0) {
        const numToLoad = Math.min(batchSize, this.assetsLoading.length);
        const batch = this.assetsLoading.splice(0, numToLoad);
        await this.loadAssets(batch);
      }
    } else {
      const numToLoad = this.assetsLoading.length; // load all assets at once.
      const batch = this.assetsLoading.splice(0, numToLoad);
      await this.loadAssets(batch);
    }
  }

  public unloadContents(assetsToUnload: IAssetBase[]) {
    // if any assets are waiting to be loaded,
    // remove them form the loading list.
    for (const assetLoading of this.assetsLoading) {
      for (const assetToUnload of assetsToUnload) {
        if (assetLoading.path === assetToUnload.path) {
          const assetLoadingIdx = this.assetsLoading.indexOf(assetLoading);
          this.assetsLoading.splice(assetLoadingIdx, 1);
        }
      }
    }
    this.unloadAssets(assetsToUnload);
  }

  //----------------------------------------------------------------
  public async loadAssets(assetsToLoad: IAssetBase[]) {
    const promises = assetsToLoad.map(assetToLoad => this.loadAsset(assetToLoad));
    await Promise.all(promises);
  }

  public async loadAsset(assetToLoad: IAssetBase) {
    const assetIdx = this.assets.findIndex(asset => asset.path === assetToLoad.path);
    if (assetIdx >= 0) {
      console.error("loader.batch.loadAsset - asset already exists: ", assetToLoad.path);
      return;
    }
    const asset: IAssetBase = {
      type: assetToLoad.type,
      path: assetToLoad.path,
      id: assetToLoad.id || assetToLoad.path, // if no unique id is provided, use path as unique id.
    };
    this.assets.push(asset);

    asset.loaded = false;
    asset.loading = true;
    asset.loadProgress = 0;

    const scene = this.context.getScene();
    const rootNode = this.context.getRootNode();

    if (asset.type === "model") {
      const assetModel = asset as IAssetModel;
      assetModel.extension = (assetToLoad as IAssetModel).extension; // copy extension from assetToLoad.
      await loadModel(assetModel, scene!, rootNode!);
    } else if (asset.type === "pointcloud") {
      const assetPointCloud = asset as IAssetPointCloud;
      assetPointCloud.size = (assetToLoad as IAssetPointCloud).size; // copy size from assetToLoad.
      assetPointCloud.color = (assetToLoad as IAssetPointCloud).color; // copy color from assetToLoad.
      await loadPointCloud(assetPointCloud, scene!, rootNode!);
    } else if (asset.type === "texture") {
      const assetTexture = asset as IAssetTexture;
      await loadTexture(assetTexture, scene!);
    } else if (asset.type === "cubetexture") {
      const assetCubeTexture = asset as IAssetCubeTexture;
      await loadCubeTexture(assetCubeTexture, scene!);
    } else if (asset.type === "video") {
      const assetVideo = asset as IAssetVideo;
      assetVideo.videoElement = (assetToLoad as IAssetVideo).videoElement; // copy videoElement from assetToLoad.
      await loadVideo(assetVideo, scene!);
    } else if (asset.type === "audio") {
      const assetAudio = asset as IAssetAudio;
      assetAudio.audioElement = (assetToLoad as IAssetAudio).audioElement; // copy audioElement from assetToLoad.
      await loadAudio(assetAudio, scene!);
    }

    asset.loaded = true;
    asset.loading = false;
    asset.loadProgress = 1;

    this.context.onAssetLoadComplete?.(asset);
  }

  //----------------------------------------------------------------
  public unloadAssets(assetsToUnload: IAssetBase[]) {
    for (const assetToUnload of assetsToUnload) {
      this.unloadAsset(assetToUnload);
    }
  }

  public unloadAsset(assetToUnload: IAssetBase) {
    const assetIdx = this.assets.findIndex(asset => asset.path === assetToUnload.path);
    if (assetIdx < 0) {
      console.error("loader.batch.unloadAsset - asset does not exists: ", assetToUnload.path);
      return;
    }
    const asset = this.assets[assetIdx];
    if (asset.type === "model") {
      const assetModel = asset as IAssetModel;
      unloadModel(assetModel);
    } else if (asset.type === "pointcloud") {
      const assetPointCloud = asset as IAssetPointCloud;
      unloadPointCloud(assetPointCloud);
    } else if (asset.type === "texture") {
      const assetTexture = asset as IAssetTexture;
      unloadTexture(assetTexture);
    } else if (asset.type === "cubetexture") {
      const assetCubeTexture = asset as IAssetCubeTexture;
      unloadCubeTexture(assetCubeTexture);
    } else if (asset.type === "video") {
      const assetVideo = asset as IAssetVideo;
      unloadVideo(assetVideo);
    } else if (asset.type === "audio") {
      const assetAudio = asset as IAssetAudio;
      unloadAudio(assetAudio);
    }
    this.assets.splice(assetIdx, 1);
  }
}

export default AssetBatch;
